import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  RefreshCw,
  Info,
  CheckCircle,
  Clock,
  Loader2,
  TrendingUp,
  Play,
  Key,
  X,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import type { RegimeScoreItem, RegimeJobRun } from '../types/api';

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const SCORE_TYPES = [
  { value: '', label: 'All types' },
  { value: 'FINAL_ALLOCATION_SCORE', label: 'Final Allocation Score' },
  { value: 'CONFIRMED_EOD', label: 'Confirmed EOD' },
  { value: 'PROVISIONAL_INTRADAY', label: 'Provisional Intraday' },
];

const LOOKBACK_MODES = [
  { value: '', label: 'All modes' },
  { value: 'FULL_CONFIG_LOOKBACK', label: 'Full Config Lookback' },
  { value: 'TRUNCATED_NEW_ASSET', label: 'Truncated New Asset' },
];

const REGIME_LABEL_META: Record<string, { color: string; bg: string; short: string; chartColor: string }> = {
  TC_BULL_PERSISTENT:    { color: 'text-emerald-700', bg: 'bg-emerald-100', short: 'Bull Persist.',   chartColor: '#d1fae5' },
  TC_BEAR_PERSISTENT:    { color: 'text-red-700',     bg: 'bg-red-100',     short: 'Bear Persist.',   chartColor: '#fee2e2' },
  MR_BALANCED_PERSISTENT:{ color: 'text-blue-700',    bg: 'bg-blue-100',    short: 'Balanced',        chartColor: '#dbeafe' },
  BULL_TRANSITION:       { color: 'text-green-600',   bg: 'bg-green-50',    short: 'Bull Trans.',     chartColor: '#f0fdf4' },
  BEAR_TRANSITION:       { color: 'text-orange-700',  bg: 'bg-orange-100',  short: 'Bear Trans.',     chartColor: '#ffedd5' },
  REGIME_SHIFT_RISK:     { color: 'text-amber-700',   bg: 'bg-amber-100',   short: 'Shift Risk',      chartColor: '#fef3c7' },
  LOW_CONVICTION:        { color: 'text-gray-600',    bg: 'bg-gray-100',    short: 'Low Conv.',       chartColor: '#f3f4f6' },
  BULLISH_TREND:         { color: 'text-emerald-800', bg: 'bg-emerald-200', short: 'Bullish Trend',   chartColor: '#a7f3d0' },
  NEUTRAL_TREND:         { color: 'text-blue-600',    bg: 'bg-blue-50',     short: 'Neutral Trend',   chartColor: '#eff6ff' },
  BEARISH_TREND:         { color: 'text-red-800',     bg: 'bg-red-200',     short: 'Bearish Trend',   chartColor: '#fecaca' },
  RISK_OFF:              { color: 'text-red-900',     bg: 'bg-red-300',     short: 'Risk Off',        chartColor: '#fca5a5' },
  RECOVERY_ATTEMPT:      { color: 'text-amber-700',   bg: 'bg-amber-50',    short: 'Recovery',        chartColor: '#fffbeb' },
};

function regimeMeta(label: string) {
  return REGIME_LABEL_META[label] ?? { color: 'text-gray-500', bg: 'bg-gray-50', short: label, chartColor: '#f9fafb' };
}

function currentRegimeFromProbs(bull: number, sideways: number, bear: number): string {
  if (bull >= sideways && bull >= bear) return 'Bull';
  if (bear >= sideways && bear >= bull) return 'Bear';
  return 'Sideways';
}

function fmt(val: number | undefined | null, decimals = 3): string {
  if (val == null) return '—';
  return val.toFixed(decimals);
}

function pct(val: number | undefined | null): string {
  if (val == null) return '—';
  return `${(val * 100).toFixed(1)}%`;
}

function fmtDate(val: string | undefined | null): string {
  if (!val) return '—';
  return val.slice(0, 10);
}

function fmtDatetime(val: string | undefined | null): string {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return val.slice(0, 19).replace('T', ' ');
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

function rawField(score: RegimeScoreItem, key: string): string {
  const raw = score.raw_model_output as Record<string, unknown> | undefined;
  if (!raw) return '—';
  const v = raw[key];
  if (v == null) return '—';
  if (typeof v === 'number') return v.toFixed(4);
  return String(v);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RegimeBadge({ label }: { label: string }) {
  const { color, bg, short } = regimeMeta(label);
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${color} ${bg} whitespace-nowrap`}>
      {short}
    </span>
  );
}

function CurrentRegimeBadge({ regime }: { regime: string }) {
  const map: Record<string, string> = {
    Bull: 'bg-emerald-100 text-emerald-800',
    Bear: 'bg-red-100 text-red-800',
    Sideways: 'bg-blue-100 text-blue-800',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${map[regime] ?? 'bg-gray-100 text-gray-600'}`}>
      {regime}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    COMPLETED:       'bg-emerald-100 text-emerald-800',
    RUNNING:         'bg-blue-100 text-blue-800',
    QUEUED:          'bg-gray-100 text-gray-700',
    FAILED:          'bg-red-100 text-red-800',
    PARTIAL_SUCCESS: 'bg-amber-100 text-amber-800',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${map[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function JobTypeIcon({ jobType }: { jobType: string }) {
  if (jobType === 'EOD') return <CheckCircle className="w-4 h-4 text-emerald-600" />;
  if (jobType === 'INTRADAY') return <Clock className="w-4 h-4 text-blue-600" />;
  if (jobType === 'FINAL_SCORE') return <Activity className="w-4 h-4 text-purple-600" />;
  return <Info className="w-4 h-4 text-gray-400" />;
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16 text-gray-400">
      <Loader2 className="w-8 h-8 animate-spin mr-3" />
      <span>Loading…</span>
    </div>
  );
}

function ErrorBox({ message, isDisabled }: { message: string; isDisabled?: boolean }) {
  return (
    <div className={`flex items-start gap-3 p-5 rounded-lg border ${isDisabled ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
      <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div>
        {isDisabled ? (
          <>
            <p className="font-semibold">Regime Dashboard Disabled</p>
            <p className="text-sm mt-1">
              This page requires <code className="font-mono bg-amber-100 px-1 rounded">ENABLE_REGIME_ENGINE=true</code> and{' '}
              <code className="font-mono bg-amber-100 px-1 rounded">ENABLE_REGIME_DASHBOARD=true</code> to be set on the server.
            </p>
          </>
        ) : (
          <>
            <p className="font-semibold">Error loading regime data</p>
            <p className="text-sm mt-1">{message}</p>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Price Chart with Regime Bands
// ---------------------------------------------------------------------------

function PriceChartWithBands({
  symbol,
  history,
}: {
  symbol: string;
  history: RegimeScoreItem[];
}) {
  const today = new Date().toISOString().slice(0, 10);
  const ninetyDaysAgo = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  })();

  const priceQuery = useQuery({
    queryKey: ['regime-price', symbol],
    queryFn: () =>
      api.getBacktestBenchmark(symbol, ninetyDaysAgo, today, 100),
    retry: false,
    staleTime: 60_000,
  });

  // Build price lookup by date
  const priceLookup = useMemo(() => {
    const m: Record<string, number> = {};
    (priceQuery.data?.points ?? []).forEach((p) => { m[p.date] = p.equity; });
    return m;
  }, [priceQuery.data]);

  // Build chart data: merge price + regime label per date (oldest first)
  const chartData = useMemo(() => {
    const sorted = [...history].reverse();
    return sorted.map((s) => ({
      date: s.as_of_date,
      price: priceLookup[s.as_of_date] ?? null,
      regime_label: s.final_regime_label ?? s.regime_label,
      markov_score: s.markov_score,
    }));
  }, [history, priceLookup]);

  // Build regime band segments for ReferenceArea
  const bandSegments = useMemo(() => {
    const segments: { x1: string; x2: string; label: string }[] = [];
    let current: { x1: string; x2: string; label: string } | null = null;
    chartData.forEach((d) => {
      if (!current || current.label !== d.regime_label) {
        if (current) segments.push(current);
        current = { x1: d.date, x2: d.date, label: d.regime_label };
      } else {
        current.x2 = d.date;
      }
    });
    if (current) segments.push(current);
    return segments;
  }, [chartData]);

  const hasPrice = chartData.some((d) => d.price != null);

  return (
    <div className="space-y-4">
      {/* Price chart */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Price with Regime Bands (last 90 days)
          </p>
          {priceQuery.isError && (
            <span className="text-xs text-amber-600">Price data unavailable</span>
          )}
        </div>
        {priceQuery.isLoading ? (
          <div className="h-32 flex items-center justify-center text-gray-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading price data…
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              {/* Regime band overlays */}
              {bandSegments.map((seg, i) => (
                <ReferenceArea
                  key={i}
                  x1={seg.x1}
                  x2={seg.x2}
                  fill={regimeMeta(seg.label).chartColor}
                  fillOpacity={0.6}
                  strokeOpacity={0}
                />
              ))}
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} width={40} domain={['auto', 'auto']} />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(v: number | undefined, name: string | undefined) => [
                  v == null ? 'N/A' : name === 'price' ? `$${v.toFixed(2)}` : v.toFixed(3),
                  name === 'price' ? 'Price (indexed)' : (name ?? ''),
                ]}
                labelFormatter={(l) => `Date: ${l}`}
              />
              {hasPrice && (
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="#1d4ed8"
                  dot={false}
                  name="price"
                  strokeWidth={2}
                  connectNulls
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Markov Score + probabilities */}
      <div>
        <p className="text-xs text-gray-500 mb-1">Markov Score + Probabilities</p>
        <ResponsiveContainer width="100%" height={150}>
          <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            {bandSegments.map((seg, i) => (
              <ReferenceArea
                key={i}
                x1={seg.x1}
                x2={seg.x2}
                fill={regimeMeta(seg.label).chartColor}
                fillOpacity={0.5}
                strokeOpacity={0}
              />
            ))}
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis domain={[-1, 1]} tick={{ fontSize: 10 }} width={36} />
            <Tooltip
              contentStyle={{ fontSize: 12 }}
              formatter={(v: number | undefined, name: string | undefined) => [v == null ? 'N/A' : v.toFixed(3), name ?? '']}
              labelFormatter={(l) => `Date: ${l}`}
            />
            <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="markov_score" stroke="#059669" dot={false} name="Markov Score" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Symbol Detail Panel
// ---------------------------------------------------------------------------

function SymbolDetailPanel({
  symbol,
  configId,
  onClose,
}: {
  symbol: string;
  configId: string;
  onClose: () => void;
}) {
  const [historyScoreType, setHistoryScoreType] = useState('FINAL_ALLOCATION_SCORE');

  const historyQuery = useQuery({
    queryKey: ['regime-history', symbol, historyScoreType, configId],
    queryFn: () =>
      api.getRegimeScoreHistory({
        symbol,
        score_type: historyScoreType || undefined,
        config_id: configId || undefined,
        limit: 90,
      }),
    retry: false,
  });

  const latestAllTypesQuery = useQuery({
    queryKey: ['regime-latest-all-types', symbol, configId],
    queryFn: () =>
      api.getRegimeLatestScores({
        config_id: configId || undefined,
        score_type: undefined,
      }),
    retry: false,
    select: (data) => data.scores.filter((s) => s.symbol === symbol),
  });

  const history = historyQuery.data?.history ?? [];

  const scoresByType = useMemo(() => {
    const m: Record<string, RegimeScoreItem> = {};
    (latestAllTypesQuery.data ?? []).forEach((s) => {
      m[s.score_type] = s;
    });
    return m;
  }, [latestAllTypesQuery.data]);

  const eodScore = scoresByType['CONFIRMED_EOD'];
  const intradayScore = scoresByType['PROVISIONAL_INTRADAY'];
  const finalScore = scoresByType['FINAL_ALLOCATION_SCORE'];

  const latestScore = history[0];

  return (
    <div className="mt-2 border border-gray-200 rounded-xl bg-gray-50 p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-600" />
          {symbol} — Detail View
          {latestScore && <RegimeBadge label={latestScore.regime_label} />}
        </h3>
        <button
          onClick={onClose}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ChevronUp className="w-4 h-4" /> Close
        </button>
      </div>

      {/* Metadata cards */}
      {latestScore && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[
            { label: 'Current Regime', value: currentRegimeFromProbs(latestScore.bull_prob, latestScore.sideways_prob, latestScore.bear_prob) },
            { label: 'Final Regime Label', value: (latestScore.final_regime_label ?? '—').replace(/_/g, ' ') },
            { label: 'Trend Regime Label', value: (latestScore.trend_regime_label ?? '—').replace(/_/g, ' ') },
            { label: 'Markov Label', value: latestScore.regime_label.replace(/_/g, ' ') },
            { label: 'Markov Score', value: fmt(latestScore.markov_score) },
            { label: 'Position Size Mult.', value: fmt(latestScore.position_size_multiplier) },
            { label: 'Persistence Score', value: fmt(latestScore.persistence_score) },
            { label: 'Transition Risk', value: fmt(latestScore.transition_risk_score) },
            { label: 'DD from 63d High', value: latestScore.drawdown_from_63d_high != null ? `${(latestScore.drawdown_from_63d_high * 100).toFixed(2)}%` : '—' },
            { label: 'Return 10d', value: latestScore.return_10d != null ? `${(latestScore.return_10d * 100).toFixed(2)}%` : '—' },
            { label: 'Return 20d', value: latestScore.return_20d != null ? `${(latestScore.return_20d * 100).toFixed(2)}%` : '—' },
            { label: 'Close vs SMA50', value: latestScore.close_vs_sma50_pct != null ? `${(latestScore.close_vs_sma50_pct * 100).toFixed(2)}%` : '—' },
            { label: 'SMA20 vs SMA50', value: latestScore.sma20_vs_sma50_pct != null ? `${(latestScore.sma20_vs_sma50_pct * 100).toFixed(2)}%` : '—' },
            { label: 'Effective Lookback Days', value: String(latestScore.effective_matrix_lookback_days) },
            { label: 'Lookback Mode', value: latestScore.lookback_mode.replace(/_/g, ' ') },
            { label: 'As Of Date', value: fmtDate(latestScore.as_of_date) },
            { label: 'Updated At', value: fmtDatetime(latestScore.updated_at) },
            { label: 'Source EOD Date', value: rawField(latestScore, 'source_eod_date') },
            { label: 'Source Intraday Date', value: rawField(latestScore, 'source_intraday_date') },
            { label: 'Anchor Lag (trading days)', value: rawField(latestScore, 'anchor_lag_trading_days') },
            { label: 'Dependency Status', value: rawField(latestScore, 'dependency_status') },
            { label: 'Raw Proxy Return', value: rawField(latestScore, 'raw_proxy_return') },
            { label: 'Adjusted Proxy Return', value: rawField(latestScore, 'adjusted_proxy_return') },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500">{label}</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5 truncate" title={value}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Transition matrix & stationary distribution */}
      {latestScore?.raw_model_output && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {!!(latestScore.raw_model_output as Record<string, unknown>)['transition_matrix'] && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Transition Matrix (Bear → Sideways → Bull)</h4>
              <TransitionMatrixTable
                matrix={(latestScore.raw_model_output as Record<string, unknown>)['transition_matrix'] as number[][]}
              />
              <p className="text-xs text-gray-400 mt-2">Diagonal = persistence; rows sum to 1</p>
            </div>
          )}
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
            {!!(latestScore.raw_model_output as Record<string, unknown>)['stationary_distribution'] && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Stationary Distribution</h4>
                <DistributionBar
                  dist={(latestScore.raw_model_output as Record<string, unknown>)['stationary_distribution'] as number[]}
                />
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Probabilities</h4>
              <div className="grid grid-cols-3 gap-2 text-center">
                {([
                  { key: 'bull_prob', label: 'Bull', color: 'text-emerald-700 bg-emerald-50' },
                  { key: 'sideways_prob', label: 'Sideways', color: 'text-blue-700 bg-blue-50' },
                  { key: 'bear_prob', label: 'Bear', color: 'text-red-700 bg-red-50' },
                ] as const).map(({ key, label, color }) => (
                  <div key={key} className={`rounded p-2 ${color}`}>
                    <p className="text-xs opacity-70">{label}</p>
                    <p className="text-sm font-bold">{pct(latestScore[key])}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Score type comparison */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Score Type Comparison</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Confirmed EOD (Anchor)', score: eodScore, color: 'border-emerald-300 bg-emerald-50', dateField: 'source_eod_date' },
            { label: 'Provisional Intraday', score: intradayScore, color: 'border-blue-300 bg-blue-50', dateField: 'source_intraday_date' },
            { label: 'Final Allocation Score', score: finalScore, color: 'border-purple-300 bg-purple-50', dateField: null },
          ].map(({ label, score, color, dateField }) => (
            <div key={label} className={`rounded-lg border-2 p-4 ${color}`}>
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">{label}</p>
              {score ? (
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {score.final_regime_label && (
                      <RegimeBadge label={score.final_regime_label} />
                    )}
                    <RegimeBadge label={score.regime_label} />
                    <CurrentRegimeBadge regime={currentRegimeFromProbs(score.bull_prob, score.sideways_prob, score.bear_prob)} />
                  </div>
                  {score.trend_regime_label && (
                    <p className="text-xs text-gray-500">Trend: <span className="font-semibold">{score.trend_regime_label.replace(/_/g, ' ')}</span></p>
                  )}
                  <p className="text-xs text-gray-600">Markov: <span className="font-semibold">{fmt(score.markov_score)}</span></p>
                  <p className="text-xs text-gray-600">
                    Bull: <span className="font-semibold text-emerald-700">{pct(score.bull_prob)}</span>{' / '}
                    Bear: <span className="font-semibold text-red-700">{pct(score.bear_prob)}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {dateField ? `Source: ${rawField(score, dateField)}` : `As of: ${fmtDate(score.as_of_date)}`}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No data available</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* History score type selector */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-gray-700">History for:</span>
        <select
          value={historyScoreType}
          onChange={(e) => setHistoryScoreType(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
          {SCORE_TYPES.slice(1).map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Price chart + regime bands */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        {historyQuery.isLoading ? (
          <LoadingSpinner />
        ) : historyQuery.isError ? (
          <div className="py-6 text-center text-red-500 text-sm">Failed to load history</div>
        ) : history.length === 0 ? (
          <div className="py-6 text-center text-gray-400 text-sm">
            No history data available for this symbol / score type.
          </div>
        ) : (
          <PriceChartWithBands symbol={symbol} history={history} />
        )}
      </div>

      {/* Probability history chart */}
      {history.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 mb-2">Bull / Sideways / Bear Probabilities (%)</p>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart
              data={[...history].reverse().map((s) => ({
                date: s.as_of_date,
                bull_prob: +(s.bull_prob * 100).toFixed(2),
                sideways_prob: +(s.sideways_prob * 100).toFixed(2),
                bear_prob: +(s.bear_prob * 100).toFixed(2),
                regime_label: s.regime_label,
              }))}
              margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={36} unit="%" />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(v: number | undefined, name: string | undefined) => [v == null ? 'N/A' : `${v.toFixed(1)}%`, name ?? '']}
                labelFormatter={(l) => `Date: ${l}`}
              />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="bull_prob" stroke="#059669" dot={false} name="Bull %" strokeWidth={2} />
              <Line type="monotone" dataKey="sideways_prob" stroke="#2563eb" dot={false} name="Sideways %" strokeWidth={1.5} />
              <Line type="monotone" dataKey="bear_prob" stroke="#dc2626" dot={false} name="Bear %" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function TransitionMatrixTable({ matrix }: { matrix: number[][] | null | undefined }) {
  if (!matrix || !Array.isArray(matrix) || matrix.length === 0)
    return <p className="text-xs text-gray-400">No matrix data</p>;
  const labels = ['Bear', 'Sideways', 'Bull'];
  return (
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr>
          <th className="text-left text-gray-400 font-normal pr-2 py-1">From ↓ To →</th>
          {labels.map((l) => <th key={l} className="px-2 py-1 text-gray-600 font-semibold">{l}</th>)}
        </tr>
      </thead>
      <tbody>
        {matrix.map((row, i) => (
          <tr key={i} className="border-t border-gray-100">
            <td className="text-gray-600 font-semibold pr-2 py-1">{labels[i]}</td>
            {row.map((v, j) => (
              <td
                key={j}
                className={`px-2 py-1 text-center rounded ${i === j ? 'bg-emerald-50 font-bold text-emerald-700' : 'text-gray-700'}`}
              >
                {(v * 100).toFixed(1)}%
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DistributionBar({ dist }: { dist: number[] | null | undefined }) {
  if (!dist || dist.length < 3) return null;
  const [bear, sideways, bull] = dist;
  return (
    <div className="space-y-1.5 text-xs">
      {[
        { label: 'Bull',     value: bull,     color: 'bg-emerald-500' },
        { label: 'Sideways', value: sideways,  color: 'bg-blue-400' },
        { label: 'Bear',     value: bear,      color: 'bg-red-400' },
      ].map(({ label, value, color }) => (
        <div key={label} className="flex items-center gap-2">
          <span className="w-14 text-gray-500">{label}</span>
          <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
            <div className={`h-full ${color}`} style={{ width: `${Math.round((value ?? 0) * 100)}%` }} />
          </div>
          <span className="w-10 text-right text-gray-700 font-semibold">
            {((value ?? 0) * 100).toFixed(1)}%
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Job Status Panel
// ---------------------------------------------------------------------------

function JobStatusPanel({ jobs, isLoading, isError }: {
  jobs: RegimeJobRun[];
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorBox message="Failed to load job history" />;
  if (jobs.length === 0)
    return <div className="text-center py-10 text-gray-400">No regime job runs found.</div>;

  const COLS = [
    'ID', 'Job Type', 'Status', '% Completed', 'Started At', 'Completed At',
    'Processed Symbols', 'Symbols Failed', 'Records Inserted', 'Records Updated', 'Error',
  ];

  function calcPct(job: RegimeJobRun): string {
    if (job.status === 'COMPLETED') return '100%';
    if (job.rows_expected != null && job.rows_expected > 0 && job.rows_created != null) {
      return `${Math.min(100, Math.round((job.rows_created / job.rows_expected) * 100))}%`;
    }
    if (job.symbols_requested != null && job.symbols_requested > 0 && job.symbols_processed != null) {
      return `${Math.min(100, Math.round((job.symbols_processed / job.symbols_requested) * 100))}%`;
    }
    return '—';
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
            {COLS.map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <td className="px-3 py-2 text-gray-500 text-xs">#{job.id}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <JobTypeIcon jobType={job.job_type} />
                  <span className="font-medium text-gray-700">{job.job_type}</span>
                </div>
              </td>
              <td className="px-3 py-2"><StatusBadge status={job.status} /></td>
              <td className="px-3 py-2 text-gray-700 text-xs font-medium">{calcPct(job)}</td>
              <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">{fmtDatetime(job.started_at ?? job.created_at)}</td>
              <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">{fmtDatetime(job.completed_at)}</td>
              <td className="px-3 py-2 text-gray-700 text-center">{job.symbols_processed ?? '—'}</td>
              <td className="px-3 py-2 text-center">
                {job.symbols_failed != null && job.symbols_failed > 0
                  ? <span className="text-red-600 font-semibold">{job.symbols_failed}</span>
                  : <span className="text-gray-400">{job.symbols_failed ?? '—'}</span>}
              </td>
              <td className="px-3 py-2 text-gray-400 text-xs text-center">{job.records_inserted ?? job.rows_created ?? '—'}</td>
              <td className="px-3 py-2 text-gray-400 text-xs text-center">{job.records_updated ?? job.rows_updated ?? '—'}</td>
              <td className="px-3 py-2 text-xs text-red-600 max-w-xs truncate" title={job.error_message ?? ''}>
                {job.error_message ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config Comparison Panel
// ---------------------------------------------------------------------------

function ConfigComparisonPanel({
  symbol,
  configs,
}: {
  symbol: string;
  configs: { config_id: string }[];
}) {
  const allScoresQuery = useQuery({
    queryKey: ['regime-config-comparison', symbol],
    queryFn: () => api.getRegimeLatestScores({ score_type: 'CONFIRMED_EOD' }),
    enabled: !!symbol && configs.length > 1,
    retry: false,
    select: (data) => data.scores.filter((s) => s.symbol === symbol),
  });

  if (configs.length <= 1 || !symbol) return null;

  const scores = allScoresQuery.data ?? [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
        <Info className="w-4 h-4 text-blue-500" />
        Config Comparison — {symbol}
      </h3>
      {allScoresQuery.isLoading ? (
        <LoadingSpinner />
      ) : scores.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No multi-config data available for this symbol.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-gray-500 border-b border-gray-200">
                {['Config', 'Current Regime', 'Regime Label', 'Markov Score', 'Bull', 'Sideways', 'Bear', 'Pos. Size Mult.'].map((h) => (
                  <th key={h} className="text-left px-3 py-2 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scores.map((s) => (
                <tr key={s.config_id} className="border-b border-gray-100">
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{s.config_id}</td>
                  <td className="px-3 py-2">
                    <CurrentRegimeBadge regime={currentRegimeFromProbs(s.bull_prob, s.sideways_prob, s.bear_prob)} />
                  </td>
                  <td className="px-3 py-2"><RegimeBadge label={s.regime_label} /></td>
                  <td className="px-3 py-2 font-semibold">{fmt(s.markov_score)}</td>
                  <td className="px-3 py-2 text-emerald-700">{pct(s.bull_prob)}</td>
                  <td className="px-3 py-2 text-blue-700">{pct(s.sideways_prob)}</td>
                  <td className="px-3 py-2 text-red-700">{pct(s.bear_prob)}</td>
                  <td className="px-3 py-2">{fmt(s.position_size_multiplier)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const REGIME_API_KEY_STORAGE = 'regime_bot_api_key';

export function RegimeScores() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  // Filter state
  const [filterStrategyId, setFilterStrategyId] = useState<string>('');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [filterScoreType, setFilterScoreType] = useState('CONFIRMED_EOD');
  const [filterConfigId, setFilterConfigId] = useState('');
  const [filterLookbackMode, setFilterLookbackMode] = useState('');

  // UI state
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<string>('symbol');
  const [sortAsc, setSortAsc] = useState(true);

  // Trigger state
  const [apiKey, setApiKey] = useState<string>('');
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [pendingTrigger, setPendingTrigger] = useState<'eod' | 'intraday' | 'final_score' | null>(null);
  const [triggerLoading, setTriggerLoading] = useState<string | null>(null);
  const [triggerMessage, setTriggerMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const triggerMsgTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(REGIME_API_KEY_STORAGE);
    if (stored) setApiKey(stored);
  }, []);

  function showTriggerMessage(type: 'success' | 'error', text: string) {
    setTriggerMessage({ type, text });
    if (triggerMsgTimer.current) clearTimeout(triggerMsgTimer.current);
    triggerMsgTimer.current = setTimeout(() => setTriggerMessage(null), 5000);
  }

  async function executeTrigger(kind: 'eod' | 'intraday' | 'final_score', key: string) {
    setTriggerLoading(kind);
    try {
      if (kind === 'eod') {
        await api.triggerRegimeEodNow(key);
        showTriggerMessage('success', 'EOD bot triggered successfully. Job queued.');
      } else if (kind === 'intraday') {
        await api.triggerRegimeIntradayNow(key);
        showTriggerMessage('success', 'Intraday bot triggered successfully. Job queued.');
      } else {
        await api.triggerRegimeFinalScoreNow(key);
        showTriggerMessage('success', 'Final Score bot triggered successfully. Job queued.');
      }
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['regime-jobs'] });
        queryClient.invalidateQueries({ queryKey: ['regime-latest-scores'] });
      }, 1000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showTriggerMessage('error', `Trigger failed: ${msg}`);
    } finally {
      setTriggerLoading(null);
    }
  }

  function handleTriggerClick(kind: 'eod' | 'intraday' | 'final_score') {
    if (!apiKey) {
      setPendingTrigger(kind);
      setApiKeyInput('');
      setShowApiKeyModal(true);
    } else {
      executeTrigger(kind, apiKey);
    }
  }

  function handleApiKeySubmit() {
    const trimmed = apiKeyInput.trim();
    if (!trimmed) return;
    localStorage.setItem(REGIME_API_KEY_STORAGE, trimmed);
    setApiKey(trimmed);
    setShowApiKeyModal(false);
    if (pendingTrigger) {
      executeTrigger(pendingTrigger, trimmed);
      setPendingTrigger(null);
    }
  }

  // Dashboard status (feature flag gate)
  const dashboardStatusQuery = useQuery({
    queryKey: ['regime-dashboard-status'],
    queryFn: () => api.getRegimeDashboardStatus(),
    retry: false,
    staleTime: 120_000,
  });

  const dashboardStatus = dashboardStatusQuery.data;
  const isDashboardEnabled = dashboardStatus?.engine_enabled && dashboardStatus?.dashboard_enabled;
  const isStatusLoaded = !dashboardStatusQuery.isLoading;

  // Data queries — only run when dashboard is enabled
  const configsQuery = useQuery({
    queryKey: ['regime-configs'],
    queryFn: () => api.getRegimeConfigs(),
    enabled: !!isDashboardEnabled,
    retry: false,
    staleTime: 60_000,
  });

  const strategiesQuery = useQuery({
    queryKey: ['symphonies-list'],
    queryFn: () => api.getSymphonies(),
    enabled: !!isDashboardEnabled,
    retry: false,
    staleTime: 60_000,
  });

  const scoresQuery = useQuery({
    queryKey: ['regime-latest-scores', filterScoreType, filterConfigId, filterStrategyId],
    queryFn: () =>
      api.getRegimeLatestScores({
        score_type: filterScoreType || undefined,
        config_id: filterConfigId || undefined,
        strategy_id: filterStrategyId ? Number(filterStrategyId) : undefined,
      }),
    enabled: !!isDashboardEnabled,
    retry: false,
    refetchInterval: 60_000,
  });

  const jobsQuery = useQuery({
    queryKey: ['regime-jobs'],
    queryFn: () => api.getRegimeJobs({ limit: 3 }),
    enabled: !!isDashboardEnabled,
    retry: false,
    refetchInterval: (query) => {
      const jobs = (query.state.data as { jobs?: { status: string }[] } | undefined)?.jobs ?? [];
      const hasActiveJob = jobs.some((j) => j.status === 'QUEUED' || j.status === 'RUNNING');
      return hasActiveJob ? 3_000 : 30_000;
    },
  });

  const configs = configsQuery.data?.configs ?? [];
  const strategies = strategiesQuery.data?.symphonies ?? [];
  const allScores = scoresQuery.data?.scores ?? [];
  const jobs = jobsQuery.data?.jobs ?? [];

  // Derive available symbols from current scores
  const availableSymbols = useMemo(
    () => [...new Set(allScores.map((s) => s.symbol))].sort(),
    [allScores]
  );

  // Filter & sort rows
  const filteredScores = useMemo(() => {
    let rows = allScores;
    if (filterSymbol) rows = rows.filter((s) => s.symbol === filterSymbol);
    if (filterLookbackMode) rows = rows.filter((s) => s.lookback_mode === filterLookbackMode);

    return [...rows].sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      if (sortCol === 'symbol')                    { va = a.symbol; vb = b.symbol; }
      else if (sortCol === 'regime_label')         { va = a.regime_label; vb = b.regime_label; }
      else if (sortCol === 'final_regime_label')   { va = a.final_regime_label ?? ''; vb = b.final_regime_label ?? ''; }
      else if (sortCol === 'trend_regime_label')   { va = a.trend_regime_label ?? ''; vb = b.trend_regime_label ?? ''; }
      else if (sortCol === 'markov_score')         { va = a.markov_score; vb = b.markov_score; }
      else if (sortCol === 'bull_prob')            { va = a.bull_prob; vb = b.bull_prob; }
      else if (sortCol === 'position_size_multiplier') { va = a.position_size_multiplier; vb = b.position_size_multiplier; }
      else if (sortCol === 'as_of_date')           { va = a.as_of_date; vb = b.as_of_date; }
      else if (sortCol === 'current_regime') {
        va = currentRegimeFromProbs(a.bull_prob, a.sideways_prob, a.bear_prob);
        vb = currentRegimeFromProbs(b.bull_prob, b.sideways_prob, b.bear_prob);
      }
      const cmp = typeof va === 'number'
        ? va - (vb as number)
        : String(va).localeCompare(String(vb));
      return sortAsc ? cmp : -cmp;
    });
  }, [allScores, filterSymbol, filterLookbackMode, sortCol, sortAsc]);

  const handleSort = (col: string) => {
    if (sortCol === col) setSortAsc((v) => !v);
    else { setSortCol(col); setSortAsc(true); }
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sortCol !== col) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const rowKey = (s: RegimeScoreItem) => `${s.symbol}|${s.config_id}|${s.score_type}`;

  // Active symbol context (from filter or expanded row)
  const activeSymbol = filterSymbol || (expandedRow?.split('|')[0] ?? '');

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-700 flex items-center justify-center shadow">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Regime Scores</h1>
            <p className="text-sm text-gray-500">Markov regime scores across symbols and strategies</p>
          </div>
        </div>
        <button
          onClick={() => { scoresQuery.refetch(); jobsQuery.refetch(); }}
          disabled={scoresQuery.isFetching || !isDashboardEnabled}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${scoresQuery.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Feature flag: loading */}
      {dashboardStatusQuery.isLoading && (
        <div className="flex items-center gap-2 text-gray-400 py-4">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Checking dashboard status…</span>
        </div>
      )}

      {/* Feature flag: disabled notice */}
      {isStatusLoaded && !isDashboardEnabled && (
        <ErrorBox
          message=""
          isDisabled
        />
      )}

      {/* Only show the rest when enabled */}
      {isDashboardEnabled && (
        <>
          {/* API error */}
          {scoresQuery.isError && (
            <ErrorBox message={String((scoresQuery.error as Error)?.message ?? 'Unknown error')} />
          )}

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap gap-3 items-end">
              {/* Strategy */}
              <div className="flex flex-col gap-1 min-w-[160px]">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Strategy</label>
                <select
                  value={filterStrategyId}
                  onChange={(e) => setFilterStrategyId(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                >
                  <option value="">All strategies</option>
                  {strategies.map((s) => (
                    <option key={s.id} value={String(s.id)}>{s.name ?? s.symphony_id}</option>
                  ))}
                </select>
              </div>

              {/* Symbol */}
              <div className="flex flex-col gap-1 min-w-[140px]">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Symbol</label>
                <select
                  value={filterSymbol}
                  onChange={(e) => { setFilterSymbol(e.target.value); setExpandedRow(null); }}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                >
                  <option value="">All symbols</option>
                  {availableSymbols.map((sym) => (
                    <option key={sym} value={sym}>{sym}</option>
                  ))}
                </select>
              </div>

              {/* Score type */}
              <div className="flex flex-col gap-1 min-w-[200px]">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Score Type</label>
                <select
                  value={filterScoreType}
                  onChange={(e) => setFilterScoreType(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                >
                  {SCORE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Config */}
              <div className="flex flex-col gap-1 min-w-[200px]">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Config</label>
                <select
                  value={filterConfigId}
                  onChange={(e) => setFilterConfigId(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                >
                  <option value="">All configs</option>
                  {configs.map((c) => (
                    <option key={c.config_id} value={c.config_id}>{c.config_id}</option>
                  ))}
                </select>
              </div>

              {/* Lookback Mode */}
              <div className="flex flex-col gap-1 min-w-[200px]">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Lookback Mode</label>
                <select
                  value={filterLookbackMode}
                  onChange={(e) => setFilterLookbackMode(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                >
                  {LOOKBACK_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {(filterStrategyId || filterSymbol || filterLookbackMode || filterConfigId) && (
                <button
                  onClick={() => { setFilterStrategyId(''); setFilterSymbol(''); setFilterLookbackMode(''); setFilterConfigId(''); setExpandedRow(null); }}
                  className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Latest Scores Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-800">
                Latest Regime Scores
                {!scoresQuery.isLoading && (
                  <span className="ml-2 text-sm font-normal text-gray-400">
                    ({filteredScores.length} row{filteredScores.length !== 1 ? 's' : ''})
                  </span>
                )}
              </h2>
              {scoresQuery.isFetching && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            </div>

            {scoresQuery.isLoading ? (
              <LoadingSpinner />
            ) : filteredScores.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>No regime scores found. Run the EOD or intraday bot to generate scores.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ minWidth: '1600px' }}>
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                      {[
                        { col: 'symbol',                    label: 'Symbol' },
                        { col: 'config_id',                 label: 'Config' },
                        { col: 'score_type',                label: 'Score Type' },
                        { col: 'current_regime',            label: 'Current Regime' },
                        { col: 'final_regime_label',        label: 'Final Label' },
                        { col: 'trend_regime_label',        label: 'Trend Label' },
                        { col: 'regime_label',              label: 'Markov Label' },
                        { col: 'markov_score',              label: 'Markov Score' },
                        { col: 'bull_prob',                 label: 'Bull %' },
                        { col: 'sideways_prob',             label: 'Sideways %' },
                        { col: 'bear_prob',                 label: 'Bear %' },
                        { col: 'persistence_score',         label: 'Persistence' },
                        { col: 'transition_risk_score',     label: 'Trans. Risk' },
                        { col: 'position_size_multiplier',  label: 'Pos. Size Mult.' },
                        { col: 'as_of_date',                label: 'As Of Date' },
                        { col: 'updated_at',                label: 'Calculated At' },
                        { col: 'src_eod',                   label: 'Src EOD Date' },
                        { col: 'src_intraday',              label: 'Src Intraday Date' },
                        { col: 'anchor_lag',                label: 'Anchor Lag' },
                        { col: 'dependency',                label: 'Dependency Status' },
                        { col: 'raw_proxy',                 label: 'Raw Proxy Ret.' },
                        { col: 'adj_proxy',                 label: 'Adj Proxy Ret.' },
                        { col: 'lookback_mode',             label: 'Lookback Mode' },
                        { col: 'effective_matrix_lookback_days', label: 'Eff. Lookback Days' },
                      ].map(({ col, label }) => (
                        <th
                          key={col}
                          className="px-3 py-2 text-left font-semibold cursor-pointer select-none hover:bg-gray-100 transition-colors whitespace-nowrap"
                          onClick={() => handleSort(col)}
                        >
                          <div className="flex items-center gap-1">
                            {label}
                            <SortIcon col={col} />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredScores.map((score) => {
                      const key = rowKey(score);
                      const isExpanded = expandedRow === key;
                      const { color } = regimeMeta(score.regime_label);
                      const currentRegime = currentRegimeFromProbs(score.bull_prob, score.sideways_prob, score.bear_prob);
                      return [
                        <tr
                          key={key}
                          onClick={() => setExpandedRow(isExpanded ? null : key)}
                          className={`border-b border-gray-100 cursor-pointer transition-colors ${
                            isExpanded ? 'bg-emerald-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className={`px-3 py-2 font-bold ${color}`}>{score.symbol}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500 whitespace-nowrap">{score.config_id}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{score.score_type}</td>
                          <td className="px-3 py-2">
                            <CurrentRegimeBadge regime={currentRegime} />
                          </td>
                          <td className="px-3 py-2">
                            {score.final_regime_label
                              ? <RegimeBadge label={score.final_regime_label} />
                              : <span className="text-xs text-gray-400">—</span>}
                          </td>
                          <td className="px-3 py-2">
                            {score.trend_regime_label
                              ? <RegimeBadge label={score.trend_regime_label} />
                              : <span className="text-xs text-gray-400">—</span>}
                          </td>
                          <td className="px-3 py-2"><RegimeBadge label={score.regime_label} /></td>
                          <td className="px-3 py-2 font-semibold text-gray-800">{fmt(score.markov_score)}</td>
                          <td className="px-3 py-2 text-emerald-700 font-medium">{pct(score.bull_prob)}</td>
                          <td className="px-3 py-2 text-blue-700">{pct(score.sideways_prob)}</td>
                          <td className="px-3 py-2 text-red-700 font-medium">{pct(score.bear_prob)}</td>
                          <td className="px-3 py-2 text-gray-700">{fmt(score.persistence_score)}</td>
                          <td className="px-3 py-2 text-gray-700">{fmt(score.transition_risk_score)}</td>
                          <td className="px-3 py-2 text-gray-700">{fmt(score.position_size_multiplier)}</td>
                          <td className="px-3 py-2 text-gray-600 text-xs">{fmtDate(score.as_of_date)}</td>
                          <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">{fmtDatetime(score.updated_at)}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{rawField(score, 'source_eod_date')}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{rawField(score, 'source_intraday_date')}</td>
                          <td className="px-3 py-2 text-gray-600 text-center">{rawField(score, 'anchor_lag_trading_days')}</td>
                          <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{rawField(score, 'dependency_status')}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-600">{rawField(score, 'raw_proxy_return')}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-600">{rawField(score, 'adjusted_proxy_return')}</td>
                          <td className="px-3 py-2">
                            {score.lookback_mode === 'TRUNCATED_NEW_ASSET' ? (
                              <span className="inline-block px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-semibold">Truncated</span>
                            ) : (
                              <span className="text-xs text-gray-500">Full</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-700 text-center">{score.effective_matrix_lookback_days}</td>
                        </tr>,
                        isExpanded && (
                          <tr key={`${key}-detail`}>
                            <td colSpan={24} className="px-4 py-0 bg-emerald-50">
                              <SymbolDetailPanel
                                symbol={score.symbol}
                                configId={score.config_id}
                                onClose={() => setExpandedRow(null)}
                              />
                            </td>
                          </tr>
                        ),
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Config Comparison */}
          {activeSymbol && configs.length > 1 && (
            <ConfigComparisonPanel symbol={activeSymbol} configs={configs} />
          )}

          {/* Active Configs Summary */}
          {configs.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-base font-bold text-gray-800 mb-4">Active Regime Configs</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {configs.map((c) => (
                  <div key={c.config_id} className="rounded-lg border border-gray-200 p-3 space-y-1.5">
                    <p className="font-semibold text-sm text-gray-800 font-mono">{c.config_id}</p>
                    <div className="grid grid-cols-2 gap-x-3 text-xs text-gray-600">
                      <span>Window: <strong>{c.window}</strong></span>
                      <span>Vol window: <strong>{c.volatility_window}</strong></span>
                      <span>Threshold: <strong>{c.threshold_multiplier}</strong></span>
                      <span>Lookback: <strong>{c.matrix_lookback_days}</strong></span>
                      <span>EOD weight: <strong>{c.eod_weight}</strong></span>
                      <span>Intraday wt: <strong>{c.intraday_weight}</strong></span>
                      <span>Max anchor lag: <strong>{c.max_eod_anchor_lookback_trading_days}</strong></span>
                      <span>HMM: <strong>{c.hmm_enabled ? 'Yes' : 'No'}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Job Status Panel */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2 flex-1 min-w-0">
                <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
                Regime Bot Job History
                {!jobsQuery.isLoading && (
                  <span className="text-sm font-normal text-gray-400">(last {jobs.length})</span>
                )}
                {jobsQuery.isFetching && <Loader2 className="w-4 h-4 animate-spin text-gray-400 ml-1" />}
              </h2>

              {/* Admin-only trigger buttons */}
              {isAdmin && (
                <div className="flex items-center gap-2 flex-wrap">
                  {([
                    { kind: 'eod' as const, label: 'Run EOD Now', color: 'bg-emerald-600 hover:bg-emerald-700', icon: <CheckCircle className="w-3.5 h-3.5" /> },
                    { kind: 'intraday' as const, label: 'Run Intraday Now', color: 'bg-blue-600 hover:bg-blue-700', icon: <Clock className="w-3.5 h-3.5" /> },
                    { kind: 'final_score' as const, label: 'Run Final Score Now', color: 'bg-purple-600 hover:bg-purple-700', icon: <Activity className="w-3.5 h-3.5" /> },
                  ]).map(({ kind, label, color, icon }) => {
                    const isRunning = jobs.some((j) => j.status === 'RUNNING');
                    const isThisLoading = triggerLoading === kind;
                    const disabled = isRunning || !!triggerLoading;
                    return (
                      <button
                        key={kind}
                        onClick={() => handleTriggerClick(kind)}
                        disabled={disabled}
                        title={isRunning ? 'A job is already running' : label}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold transition-colors ${color} disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {isThisLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : icon}
                        {label}
                      </button>
                    );
                  })}
                  {apiKey && (
                    <button
                      onClick={() => {
                        localStorage.removeItem(REGIME_API_KEY_STORAGE);
                        setApiKey('');
                      }}
                      title="Clear stored API key"
                      className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 text-xs transition-colors"
                    >
                      <Key className="w-3.5 h-3.5" />
                      Clear key
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Trigger feedback message */}
            {triggerMessage && (
              <div className={`mx-4 mt-3 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 ${
                triggerMessage.type === 'success'
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                {triggerMessage.type === 'success'
                  ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
                {triggerMessage.text}
              </div>
            )}

            <div className="p-4">
              <JobStatusPanel
                jobs={jobs}
                isLoading={jobsQuery.isLoading}
                isError={jobsQuery.isError}
              />
            </div>
          </div>

          {/* Legend */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Regime Label Legend</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(REGIME_LABEL_META).map(([label, { color, bg, short }]) => (
                <div key={label} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${bg}`}>
                  <span className={`text-xs font-bold ${color}`}>{short}</span>
                  <span className="text-xs text-gray-400">—</span>
                  <span className="text-xs text-gray-500">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* API Key Modal */}
      {showApiKeyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Key className="w-5 h-5 text-gray-600" />
                Enter Regime Bot API Key
              </h3>
              <button
                onClick={() => { setShowApiKeyModal(false); setPendingTrigger(null); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500">
              Enter the <code className="font-mono bg-gray-100 px-1 rounded">REGIME_BOT_API_KEY</code> to
              authenticate the trigger request. The key will be saved locally for this session.
            </p>
            <input
              type="password"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApiKeySubmit()}
              placeholder="Paste API key here…"
              autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowApiKeyModal(false); setPendingTrigger(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApiKeySubmit}
                disabled={!apiKeyInput.trim()}
                className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                Save & Trigger
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
